import React, { useMemo } from "react";
import "./OverviewPage.scss";

import { ReactComponent as PulseIcon } from "../../../assets/icons/patient-layout-v2/overview/PulseIcon.svg";
import { ReactComponent as AppleIcon } from "../../../assets/icons/patient-layout-v2/overview/AppleIcon.svg";
import { ReactComponent as DumbbellIcon } from "../../../assets/icons/patient-layout-v2/overview/DumbbellIcon.svg";
import { ReactComponent as GaugeIcon } from "../../../assets/icons/patient-layout-v2/overview/GaugeIcon.svg";
import { ReactComponent as ArrowRightIcon } from "../../../assets/icons/patient-layout-v2/overview/ArrowRightIcon.svg";
import { RootState } from "../../../store/store";
import { useSelector } from "react-redux";
import { formatDateTime, getAgeByDob } from "../../../utils/date-and-time";
import {
  getIndication,
  getPlanDevices,
  getPlanStatus,
  isPlanPaused,
} from "../../../utils/patient";
import Button from "../../../atoms/Button/Button";
import { clampPct, useAssessmentProgress } from "../CoachAssessment/AssessmentProgressContext";
import type { CareTriadKey } from "../CoachAssessment/AssessmentProgressContext";
import PageHeader from "../../../molecules/PatientDetailsV2/PageHeader/PageHeader";

interface Stat {
  label: string;
  value: string;
  unit?: string;
  /** Long text reads at a smaller size than a figure. */
  isText?: boolean;
  /** Chip in the tile's top-right corner, alongside the label. */
  badge?: { text: string; tone: "ok" | "warn" | "bad" };
}

const EMPTY = "--";

const PLAN_TONES: Record<string, "ok" | "warn" | "bad"> = {
  Active: "ok",
  "Not started": "warn",
  Paused: "warn",
  Expired: "bad",
};

const sexLetter = (gender?: string | null) => {
  const value = String(gender || "").trim().toUpperCase();
  if (value === "M" || value === "MALE") return "M";
  if (value === "F" || value === "FEMALE") return "F";
  return "";
};

const planDate = (timestamp?: string | null) => {
  if (!timestamp) return "";
  const formatted = formatDateTime({
    timestamp,
    format: 12,
    showTime: false,
  });
  return formatted === "Invalid Date" ? "" : formatted;
};

/**
 * Derived per render rather than written into a shared object: the stats
 * belong to the patient currently loaded, and a module-level record would both
 * miss the first paint and carry one patient's numbers into the next.
 */
const buildStats = (data: any): Stat[] => {
  const age = getAgeByDob(data?.dob ?? null);
  const sex = sexLetter(data?.gender);
  const ageSex = [age != null ? String(age) : "", sex].filter(Boolean).join(" · ");
  const planStatus = getPlanStatus(data);
  const devices = getPlanDevices(data);
  const planDuration =
    [planDate(data?.plan_start_datetime), planDate(data?.plan_expiry_datetime || data?.expiry_date)]
      .filter(Boolean)
      .join(" - ") || EMPTY;
  // const { bmi, waist, glp1_stage, lwaas } = data || {};

  return [
    { label: "Age / Sex", value: ageSex || EMPTY },
    {
      label: "Weight",
      value: data?.weight ? String(data.weight) : EMPTY,
      unit: data?.weight ? data?.weight_unit || "kg" : undefined,
    },
    {
      label: "Indication",
      value: getIndication(data) || EMPTY,
      isText: true,
    },
    {
      label: "Plan",
      value: data?.plan_name || EMPTY,
      isText: true,
      badge: planStatus
        ? { text: planStatus, tone: PLAN_TONES[planStatus] }
        : undefined,
    },
    {
      label: "Plan Duration",
      value: planDuration,
      isText: true,
    },
    {
      label: "Devices",
      value: devices.length ? devices.join(", ") : "None",
      isText: true,
    },
    // { label: "BMI", value: bmi?.toString() || EMPTY },
    // { label: "Waist", value: waist?.toString() || EMPTY, unit: "cm" },
    // { label: "GLP-1 stage", value: glp1_stage || EMPTY },
    // { label: "LWAAS", value: `${lwaas} / 125` },
  ];
};

const COACHES = [
  {
    key: "diet",
    color: "var(--diet)",
    colorTint: "#e6f0e8",
    role: "Diet Coach",
    name: "Nutrition & Metabolic",
    desc: "Food frequency, 24h recall, lifestyle, weight goals and the full lab panel.",
    icon: <AppleIcon />,
  },
  {
    key: "exercise",
    color: "var(--exercise)",
    colorTint: "rgba(46, 142, 146, 0.1)",
    role: "Exercise Coach",
    name: "Physio & Movement",
    desc: "GLP-1 exercise concerns, vitals, fitness battery and FITT prescription.",
    icon: <DumbbellIcon />,
  },
  {
    key: "success",
    color: "var(--success)",
    colorTint: "#f3eef8",
    role: "Success Coach",
    name: "LWAAS Behavioural",
    desc: "25-item well-being & adherence scale with live domain scoring.",
    icon: <GaugeIcon />,
  },
];

interface Props {
  /** Supplied by PatientLayout, which owns the active view. */
  onNavigate?: (view: string) => void;
}

const OverviewPage: React.FC<Props> = ({ onNavigate }) => {
  const { patientDetails } = useSelector((state: RootState) => state.patient);
  const { pctByView, labelByView } = useAssessmentProgress();

  const stats = useMemo(
    () => buildStats(patientDetails?.data),
    [patientDetails],
  );
  const planPaused = isPlanPaused(patientDetails?.data);

  return (
    <div className="overview-page">
      <PageHeader
        title="Patient Overview"
        notice={
          planPaused ? (
            <span className="page-plan-paused">Plan paused</span>
          ) : null
        }
      />

      {planPaused && (
        <div className="ov-plan-paused" role="status">
          <span className="ov-plan-paused-label">Plan paused</span>
          <span className="ov-plan-paused-text">
            This patient&apos;s program is on pause. They are not progressing
            through the plan until it is resumed.
          </span>
        </div>
      )}

      <div className="ov-snapshot">
        <div className="ov-snap-head">
          <div className="ov-snap-title">
            <PulseIcon />
            Clinical Snapshot
          </div>
        </div>
        <div className="ov-stats">
          {stats.map((stat) => (
            <div className="stat" key={stat.label}>
              <div className="stat-head">
                <div className="stat-label">{stat.label}</div>
                {stat.badge && (
                  <span className={`stat-pill ${stat.badge.tone}`}>
                    {stat.badge.text}
                  </span>
                )}
              </div>
              <div
                className={`stat-val ${stat.isText ? "text" : ""}`}
                title={stat.isText ? stat.value : undefined}
              >
                {stat.value}
                {stat.unit && <small className="stat-unit">{stat.unit}</small>}
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="ov-grid">
        {COACHES.map((coach) => {
          const key = coach.key as CareTriadKey;
          const progress = clampPct(pctByView[key]);
          const visit = labelByView[key];
          return (
            <div
              key={coach.key}
              className="ov-coach"
              style={{
                ["--c" as any]: coach.color,
                ["--c-50" as any]: coach.colorTint,
              }}
            >
              <div className="ov-coach-ico">{coach.icon}</div>
              <div className="ov-role">{coach.role}</div>
              <div className="ov-coach-name">{coach.name}</div>
              <div className="ov-coach-desc">{coach.desc}</div>
              <div className="ov-prog">
                {visit ? <span className="ov-visit">{visit}</span> : null}
                <div className="ov-track">
                  <div
                    className="ov-fill"
                    style={{ width: `${progress}%` }}
                  />
                </div>
                <div className="ov-pct">{progress}%</div>
              </div>
              <Button
                variant="text"
                size="sm"
                color={coach.color}
                onClick={() =>
                  onNavigate?.(
                    visit.startsWith("Follow-up")
                      ? coach.key
                      : `${coach.key}-baseline`,
                  )
                }
                iconEnd={<ArrowRightIcon />}
              >
                Open assessment
              </Button>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default OverviewPage;
